import { ApolloClient } from '@apollo/client'
import { produce } from 'immer'
import { WritableDraft } from 'immer/dist/internal'
import { v1 as uuid } from 'uuid'

import {
  AnnotationFrameGroupInfoFragment,
  AnnotationFrameGroupInfoFragmentDoc,
  AnnotationSubjectFragment,
  BaseAnnotationFragment,
  GetAnnotationDotsQuery,
  GetAnnotationDotsQueryVariables,
  GetAnnotationsQuery,
  GetAnnotationsQueryVariables,
  GetPreviewAnnotationDotsDocument,
  GetPreviewAnnotationDotsQuery,
  GetPreviewAnnotationDotsQueryVariables,
  GetPreviewAnnotationsDocument,
  GetPreviewAnnotationsQuery,
  GetPreviewAnnotationsQueryVariables,
} from '@sketch/gql-types'

import {
  errorPreventiveFragmentRead,
  removeFromPaginated,
  getObservableQueriesByOperationName,
  getAnnotationDot,
  getBaseAnnotation,
  updateAnnotationsAllOrder,
  updateAnnotationsDotsQuery,
  updateAnnotationsInboxOrder,
  updateAnnotationsQuery,
  isSameAnnotationSubject,
  getCacheInputFromAnnotationSubject,
} from '@sketch/modules-common'
import { dataIdFromObject } from '@sketch/graphql-cache'

import { AnnotationSubjectInput, CacheAnnotationSubjectInput } from '../types'

interface AdditionalProps {
  shareIdentifier: string
  versionIdentifier?: string | null
  previewIdentifier?: string
}

const isAnnotationSubjectInVariablesContext = (
  queryInput: AnnotationSubjectInput,
  cacheAnnotationSubject: CacheAnnotationSubjectInput
) => {
  const { type, permanentId } = queryInput

  if (cacheAnnotationSubject?.type === 'ARTBOARD') {
    return (
      (type === 'ARTBOARD' &&
        cacheAnnotationSubject.permanentId === permanentId) ||
      (type === 'PAGE' &&
        cacheAnnotationSubject.permanentPageId === permanentId)
    )
  }

  return cacheAnnotationSubject?.permanentId === permanentId
}

// Helper to safely get annotation status from either preview or regular queries
const getAnnotationStatus = (
  variables:
    | GetAnnotationsQueryVariables
    | GetAnnotationDotsQueryVariables
    | GetPreviewAnnotationsQueryVariables
    | GetPreviewAnnotationDotsQueryVariables
) => {
  if ('annotationStatus' in variables) {
    return variables.annotationStatus
  }
  if ('status' in variables) {
    return variables.status
  }
  return undefined
}

// Helper to safely get sort order from either preview or regular queries
const getSortOrder = (
  variables:
    | GetAnnotationsQueryVariables
    | GetAnnotationDotsQueryVariables
    | GetPreviewAnnotationsQueryVariables
    | GetPreviewAnnotationDotsQueryVariables
) => {
  if ('sort' in variables) {
    return variables.sort
  }
  if ('sortOrder' in variables) {
    return variables.sortOrder
  }
  return undefined
}

export const doesAnnotationBelongToQueryVariables = (
  { shareIdentifier, versionIdentifier, previewIdentifier }: AdditionalProps,
  variables:
    | GetAnnotationsQueryVariables
    | GetAnnotationDotsQueryVariables
    | GetPreviewAnnotationsQueryVariables
    | GetPreviewAnnotationDotsQueryVariables
    | undefined,
  cacheAnnotationSubject: CacheAnnotationSubjectInput
) => {
  if (!variables) {
    return false
  }

  // Check if this is a preview query (has previewIdentifier)
  const hasPreviewIdentifier = 'previewIdentifier' in variables

  // For preview queries, match against previewIdentifier
  // For regular queries, match against shareIdentifier
  const identifierMatches = hasPreviewIdentifier
    ? (variables as GetPreviewAnnotationsQueryVariables).previewIdentifier ===
      (previewIdentifier || shareIdentifier)
    : variables.shareIdentifier === shareIdentifier

  // Version identifier check is only for regular queries (previews don't use versionIdentifier)
  const versionMatches = hasPreviewIdentifier
    ? true
    : variables.versionIdentifier === versionIdentifier

  if (!identifierMatches || !versionMatches) {
    return false
  }

  const inContextSubjects = (variables.subjects || [])?.filter(
    (subject): subject is NonNullable<typeof subject> =>
      !!subject &&
      isAnnotationSubjectInVariablesContext(
        subject as AnnotationSubjectInput,
        cacheAnnotationSubject
      )
  )

  return inContextSubjects.length > 0
}

export const createNewAnnotationInCache = (
  apollo: ApolloClient<object>,
  additionalProps: AdditionalProps,
  annotation: BaseAnnotationFragment,
  isPreview = false
) => {
  /**
   * In order to successfully add an annotation to the list
   * we need to:
   * [programmatically]
   * - add the annotation to its annotations listing [1]
   * - add to the annotation dots query [2]
   *
   * [fragment BaseAnnotation refetch]
   * - update the subject annotation counters
   */

  // Ensure annotation has all required fields for cache writing
  const normalizedAnnotation = produce(annotation, (draft: any) => {
    // Ensure comments structure has all required fields
    if (draft.comments) {
      if (!draft.comments.entries) {
        draft.comments.entries = []
      }
      if (draft.comments.meta && draft.comments.meta.after === undefined) {
        draft.comments.meta.after = null
      }
    }

    // Ensure currentSubject has required fields
    // otherwise we'll get cache writing errors
    if (draft.currentSubject) {
      if (draft.currentSubject.__typename === 'Artboard') {
        // Artboards need both uuid and identifier
        if (!('uuid' in draft.currentSubject)) {
          draft.currentSubject.uuid = uuid()
        }
        if (!('identifier' in draft.currentSubject)) {
          draft.currentSubject.identifier = uuid()
        }
        // Ensure the nested page also has uuid
        if (
          draft.currentSubject.page &&
          draft.currentSubject.page.__typename === 'Page'
        ) {
          if (
            !draft.currentSubject.page.uuid &&
            draft.currentSubject.page.pageUUID
          ) {
            draft.currentSubject.page.uuid = draft.currentSubject.page.pageUUID
          } else if (!draft.currentSubject.page.uuid) {
            draft.currentSubject.page.uuid = uuid()
          }
        }
      } else if (draft.currentSubject.__typename === 'Page') {
        // Pages as currentSubject need uuid field
        if (!draft.currentSubject.uuid && draft.currentSubject.pageUUID) {
          draft.currentSubject.uuid = draft.currentSubject.pageUUID
        } else if (!draft.currentSubject.uuid) {
          draft.currentSubject.uuid = uuid()
        }
      }
    }

    // Ensure page has uuid field
    if (draft.page && draft.page.__typename === 'Page') {
      if (!draft.page.uuid && draft.page.pageUUID) {
        draft.page.uuid = draft.page.pageUUID
      } else if (!draft.page.uuid) {
        draft.page.uuid = uuid()
      }
    }
  })

  const annotationSubject =
    getCacheInputFromAnnotationSubject(normalizedAnnotation)
  if (annotationSubject === null) {
    /**
     * If the subject is "UnprocessedSubject" which should never be the case (for FE)
     * "getCacheInputFromAnnotationSubject" will return null.
     * Since FE can't handle this state, there is no need to proceed with the
     * cache operations
     */
    return
  }

  /**
   * Get the cached/live queries that use supply annotation list data
   * and update them with the new annotation, checking if its subject
   * matches any of the query subjects.
   *
   * We check if the annotation subject and validate if its the same queried one
   * or belongs to it (artboards and pages).
   */
  const listOperationName = isPreview
    ? 'getPreviewAnnotations'
    : 'getAnnotations'
  const cachedListsQueriesToAdd = getObservableQueriesByOperationName(
    apollo,
    listOperationName,
    ({ variables }) =>
      doesAnnotationBelongToQueryVariables(
        additionalProps,
        variables,
        annotationSubject
      ) &&
      !!variables &&
      getAnnotationStatus(variables) !== 'RESOLVED_ONLY'
  )

  const addNewAnnotationQuery = ({
    annotations,
  }: WritableDraft<GetAnnotationsQuery | GetAnnotationDotsQuery>) => {
    /**
     * Double check if the annotation already exists
     * because if it does we shouldn't re-add it
     */
    const doesAnnotationAlreadyExist = annotations.entries.find(
      ({ identifier }) => normalizedAnnotation.identifier === identifier
    )

    if (!doesAnnotationAlreadyExist) {
      annotations.entries.unshift(normalizedAnnotation)
      annotations.meta.totalCount++
    }
  }

  /**
   * Iterate over the queries that are related with this annotation subject
   * and add it
   */
  cachedListsQueriesToAdd.forEach(({ variables }) => {
    if (!variables) {
      return
    }

    if (isPreview) {
      // For preview queries, manually update the cache
      const previewVariables = variables as GetPreviewAnnotationsQueryVariables
      const query = apollo.readQuery<
        GetPreviewAnnotationsQuery,
        GetPreviewAnnotationsQueryVariables
      >({
        query: GetPreviewAnnotationsDocument,
        variables: previewVariables,
      })

      if (query) {
        apollo.writeQuery<
          GetPreviewAnnotationsQuery,
          GetPreviewAnnotationsQueryVariables
        >({
          query: GetPreviewAnnotationsDocument,
          variables: previewVariables,
          data: produce(query, draft => {
            addNewAnnotationQuery(draft)
          }),
        })
      }
    } else {
      // Use existing helpers for regular queries
      if (
        getSortOrder(variables) === 'NEW_FIRST' &&
        getAnnotationStatus(variables) === 'ACTIVE_ONLY'
      ) {
        updateAnnotationsInboxOrder(
          apollo,
          variables as GetAnnotationsQueryVariables,
          normalizedAnnotation
        )
      } else {
        updateAnnotationsQuery(
          apollo,
          variables as GetAnnotationsQueryVariables,
          addNewAnnotationQuery
        )
      }
    }
  })

  /**
   * Get the cached/live queries that use supply annotation dots data
   * and update them with the new annotation, checking if its subject
   * matches any of the query subjects.
   *
   * We check if the annotation subject and validate if its the same queried one
   * or belongs to it (artboards and pages).
   */
  const dotOperationName = isPreview
    ? 'getPreviewAnnotationDots'
    : 'getAnnotationDots'
  const cachedDotQueriesToAdd = getObservableQueriesByOperationName(
    apollo,
    dotOperationName,
    ({ variables }) =>
      doesAnnotationBelongToQueryVariables(
        additionalProps,
        variables,
        annotationSubject
      ) &&
      !!variables &&
      getAnnotationStatus(variables) !== 'RESOLVED_ONLY'
  )

  /**
   * Iterate over the queries that are related with this annotation subject
   * and add it
   */
  cachedDotQueriesToAdd.forEach(({ variables }) => {
    if (!variables) {
      return
    }

    if (isPreview) {
      // For preview queries, manually update the cache
      const previewVariables =
        variables as GetPreviewAnnotationDotsQueryVariables
      const query = apollo.readQuery<
        GetPreviewAnnotationDotsQuery,
        GetPreviewAnnotationDotsQueryVariables
      >({
        query: GetPreviewAnnotationDotsDocument,
        variables: previewVariables,
      })

      if (query) {
        apollo.writeQuery<
          GetPreviewAnnotationDotsQuery,
          GetPreviewAnnotationDotsQueryVariables
        >({
          query: GetPreviewAnnotationDotsDocument,
          variables: previewVariables,
          data: produce(query, draft => {
            // Wrap draft to provide .annotations for callbacks expecting regular queries
            addNewAnnotationQuery({
              annotations: draft.previewAnnotations,
            } as any)
          }),
        })
      }
    } else {
      // Use existing helpers for regular queries
      updateAnnotationsDotsQuery(
        apollo,
        variables as GetAnnotationDotsQueryVariables,
        addNewAnnotationQuery
      )
    }
  })
}

export const moveAnnotationInCache = (
  apollo: ApolloClient<object>,
  additionalProps: AdditionalProps,
  annotation: BaseAnnotationFragment,
  cachedAnnotation?: BaseAnnotationFragment,
  isPreview = false
) => {
  /**
   * In order to successfully move an annotation
   * we need to:
   * [programmatically]
   * - check if the subject has changed and remove/add to its query [1]
   * - update the older annotation counter [2]
   *
   * [fragment BaseAnnotation refetch]
   * - update new subject annotation counters
   */

  /**
   * [1] check if the subject has changed and remove/add to its query
   *
   * We are fetching the current annotation from cache (before applying the subscription data)
   * to double-check if the subject (artboard/page) have changed, if it has we need to update the
   * queries
   */

  const annotationSubject = getCacheInputFromAnnotationSubject(annotation)

  if (annotationSubject === null) {
    /**
     * If the subject is "UnprocessedSubject", which should never happen (for FE)
     * "getCacheInputFromAnnotationSubject" will return null, because FE can't represent this
     * no need to proceed with the cache operations
     */
    return
  }

  if (
    cachedAnnotation &&
    isSameAnnotationSubject(annotation, cachedAnnotation)
  ) {
    /**
     * If the annotation.currentArtboard is the same this
     * means the annotation only was moved inside the artboard
     * so we don't need to add additional logic.
     *
     * If it's a move in a page annotation this will still be null and
     * the logic still applies
     */
    return
  }

  const listOperationName = isPreview
    ? 'getPreviewAnnotations'
    : 'getAnnotations'
  const cachedListsQueriesToAdd = getObservableQueriesByOperationName(
    apollo,
    listOperationName,
    ({ variables }) =>
      doesAnnotationBelongToQueryVariables(
        additionalProps,
        variables,
        annotationSubject
      )
  )

  cachedListsQueriesToAdd.forEach(({ variables }) => {
    if (!variables) {
      return
    }

    if (isPreview) {
      // For preview queries, manually update the cache
      const previewVariables = variables as GetPreviewAnnotationsQueryVariables
      const query = apollo.readQuery<
        GetPreviewAnnotationsQuery,
        GetPreviewAnnotationsQueryVariables
      >({
        query: GetPreviewAnnotationsDocument,
        variables: previewVariables,
      })

      if (query) {
        apollo.writeQuery<
          GetPreviewAnnotationsQuery,
          GetPreviewAnnotationsQueryVariables
        >({
          query: GetPreviewAnnotationsDocument,
          variables: previewVariables,
          data: produce(query, draft => {
            const addAnnotation = ({
              annotations,
            }: WritableDraft<GetAnnotationsQuery | GetAnnotationDotsQuery>) => {
              const doesAnnotationAlreadyExist = annotations.entries.find(
                ({ identifier }) => annotation.identifier === identifier
              )
              if (!doesAnnotationAlreadyExist) {
                annotations.entries.unshift(annotation)
                annotations.meta.totalCount++
              }
            }
            addAnnotation(draft)
          }),
        })
      }
    } else {
      // Use existing helpers for regular queries
      if (
        getSortOrder(variables) === 'NEW_FIRST' &&
        getAnnotationStatus(variables) === 'ACTIVE_ONLY'
      ) {
        // Add to the Active dot list
        updateAnnotationsInboxOrder(
          apollo,
          variables as GetAnnotationsQueryVariables,
          annotation
        )
      } else {
        updateAnnotationsAllOrder(
          apollo,
          variables as GetAnnotationsQueryVariables,
          annotation
        )
      }
    }
  })

  const dotOperationName = isPreview
    ? 'getPreviewAnnotationDots'
    : 'getAnnotationDots'
  const cachedDotQueriesToAdd = getObservableQueriesByOperationName(
    apollo,
    dotOperationName,
    ({ variables }) =>
      doesAnnotationBelongToQueryVariables(
        additionalProps,
        variables,
        annotationSubject
      )
  )

  const addAnnotation = ({
    annotations,
  }: WritableDraft<GetAnnotationsQuery | GetAnnotationDotsQuery>) => {
    /**
     * Double check if the annotation already exists
     * because if it does we shouldn't re-add it
     */
    const doesAnnotationAlreadyExist = annotations.entries.find(
      ({ identifier }) => annotation.identifier === identifier
    )

    if (!doesAnnotationAlreadyExist) {
      annotations.entries.unshift(annotation)
      annotations.meta.totalCount++
    }
  }

  cachedDotQueriesToAdd.forEach(({ variables }) => {
    if (!variables) {
      return
    }

    if (isPreview) {
      // For preview queries, manually update the cache
      const previewVariables =
        variables as GetPreviewAnnotationDotsQueryVariables
      const query = apollo.readQuery<
        GetPreviewAnnotationDotsQuery,
        GetPreviewAnnotationDotsQueryVariables
      >({
        query: GetPreviewAnnotationDotsDocument,
        variables: previewVariables,
      })

      if (query) {
        apollo.writeQuery<
          GetPreviewAnnotationDotsQuery,
          GetPreviewAnnotationDotsQueryVariables
        >({
          query: GetPreviewAnnotationDotsDocument,
          variables: previewVariables,
          data: produce(query, draft => {
            // Wrap draft to provide .annotations for callbacks expecting regular queries
            addAnnotation({
              annotations: draft.previewAnnotations,
            } as any)
          }),
        })
      }
    } else {
      // Use existing helpers for regular queries
      updateAnnotationsDotsQuery(
        apollo,
        variables as GetAnnotationDotsQueryVariables,
        addAnnotation
      )
    }
  })

  /**
   * Clean up time!
   *
   * In here we start to remove the old annotation references from the queries
   * of the previous relatable subjects, if the query already existed on the cache
   * because if it didn't... then there's nothing to do
   */
  if (!cachedAnnotation) {
    return
  }

  const annotationToRemove =
    getCacheInputFromAnnotationSubject(cachedAnnotation)

  if (!annotationToRemove) {
    return
  }

  const deleteAnnotation = ({
    annotations,
  }: WritableDraft<GetAnnotationsQuery | GetAnnotationDotsQuery>) => {
    annotations.entries = annotations.entries.filter(
      ({ identifier }) => identifier !== annotation.identifier
    )
    annotations.meta.totalCount--
  }

  const cachedListsQueriesToRemove = getObservableQueriesByOperationName(
    apollo,
    listOperationName,
    query => {
      const annotationBelongsToQuery = doesAnnotationBelongToQueryVariables(
        additionalProps,
        query.variables,
        annotationToRemove
      )

      return (
        annotationBelongsToQuery && !cachedListsQueriesToAdd.includes(query)
      )
    }
  )

  cachedListsQueriesToRemove.forEach(({ variables }) => {
    if (!variables) {
      return
    }

    if (isPreview) {
      // For preview queries, manually update the cache
      const previewVariables = variables as GetPreviewAnnotationsQueryVariables
      const query = apollo.readQuery<
        GetPreviewAnnotationsQuery,
        GetPreviewAnnotationsQueryVariables
      >({
        query: GetPreviewAnnotationsDocument,
        variables: previewVariables,
      })

      if (query) {
        apollo.writeQuery<
          GetPreviewAnnotationsQuery,
          GetPreviewAnnotationsQueryVariables
        >({
          query: GetPreviewAnnotationsDocument,
          variables: previewVariables,
          data: produce(query, draft => {
            deleteAnnotation(draft)
          }),
        })
      }
    } else {
      // Use existing helpers for regular queries
      updateAnnotationsQuery(
        apollo,
        variables as GetAnnotationsQueryVariables,
        deleteAnnotation
      )
    }
  })

  const cachedDotQueriesToRemove = getObservableQueriesByOperationName(
    apollo,
    dotOperationName,
    query => {
      const annotationBelongsToQuery = doesAnnotationBelongToQueryVariables(
        additionalProps,
        query.variables,
        annotationToRemove
      )

      return annotationBelongsToQuery && !cachedDotQueriesToAdd.includes(query)
    }
  )

  cachedDotQueriesToRemove.forEach(({ variables }) => {
    if (!variables) {
      return
    }

    if (isPreview) {
      // For preview queries, manually update the cache
      const previewVariables =
        variables as GetPreviewAnnotationDotsQueryVariables
      const query = apollo.readQuery<
        GetPreviewAnnotationDotsQuery,
        GetPreviewAnnotationDotsQueryVariables
      >({
        query: GetPreviewAnnotationDotsDocument,
        variables: previewVariables,
      })

      if (query) {
        apollo.writeQuery<
          GetPreviewAnnotationDotsQuery,
          GetPreviewAnnotationDotsQueryVariables
        >({
          query: GetPreviewAnnotationDotsDocument,
          variables: previewVariables,
          data: produce(query, draft => {
            // Wrap draft to provide .annotations for callbacks expecting regular queries
            deleteAnnotation({
              annotations: draft.previewAnnotations,
            } as any)
          }),
        })
      }
    } else {
      // Use existing helpers for regular queries
      updateAnnotationsDotsQuery(
        apollo,
        variables as GetAnnotationDotsQueryVariables,
        deleteAnnotation
      )
    }
  })

  /**
   * [2] update the older annotation counter
   */
  if (cachedAnnotation?.currentSubject?.__typename === 'Artboard') {
    const { documentVersionShortId, permanentArtboardShortId } =
      cachedAnnotation.currentSubject

    const artboardId = dataIdFromObject({
      __typename: 'Artboard',
      documentVersionShortId,
      permanentArtboardShortId,
    })!

    const artboardCounters =
      errorPreventiveFragmentRead<AnnotationFrameGroupInfoFragment>(apollo, {
        fragment: AnnotationFrameGroupInfoFragmentDoc,
        fragmentName: 'AnnotationFrameGroupInfo',
        id: artboardId,
      })

    artboardCounters &&
      apollo.writeFragment<AnnotationFrameGroupInfoFragment>({
        fragment: AnnotationFrameGroupInfoFragmentDoc,
        fragmentName: 'AnnotationFrameGroupInfo',
        id: artboardId,
        data: {
          ...artboardCounters,
          annotationCount: Math.max(artboardCounters.annotationCount - 1, 0),
          unreadCount: Math.max(artboardCounters.unreadCount - 1, 0),
        },
      })
  }
}

export const resolveAnnotation = (
  apollo: ApolloClient<object>,
  additionalProps: AdditionalProps,
  annotation: AnnotationSubjectFragment,
  isPreview = false
) => {
  /**
   * In order to successfully resolve an annotation
   * we need to:
   * [programmatically]
   * - remove the annotation from all the active queries [1]
   * - add the annotation to the resolved queries [2]
   *
   * [fragment BaseAnnotation refetch]
   * - Update the subject annotation counters
   * - Update the resolution status
   */

  const { identifier } = annotation

  // [1] Remove the annotation from all the active queries
  removeFromPaginated(apollo, { __typename: 'Annotation', identifier }, key =>
    key.includes('ACTIVE_ONLY')
  )

  // [2] Add the annotation to the resolved queries
  const annotationSubject = getCacheInputFromAnnotationSubject(annotation)

  if (annotationSubject === null) {
    /**
     * If the subject is "UnprocessedSubject" which should never be the case (for FE)
     * "getCacheInputFromAnnotationSubject" will return null.
     * Since FE can't handle this state, there is no need to proceed with the
     * cache operations
     */
    return
  }

  /**
   * To prevent including the "BaseAnnotation" fragment on the
   * "resolveAnnotation" mutation, it would force a huge optimistic response,
   * it was opted to get the base annotation fragment isolated.
   *
   * Since this fragment will only be relevant when the resolved sidebar is visible,
   * this doesn't occur when you are resolving the annotation on the active sidebar (filter)
   *
   * If the user is resolving a annotation (in the popover) with the resolved sidebar visible on the side
   * the "annotationComments" query already includes the "BaseAnnotation" fragment
   * to make sure the sidebar is updated has it included
   */
  const baseAnnotation = getBaseAnnotation(apollo, annotation.identifier)
  if (baseAnnotation) {
    const listOperationName = isPreview
      ? 'getPreviewAnnotations'
      : 'getAnnotations'
    const cachedListQueriesToAdd = getObservableQueriesByOperationName(
      apollo,
      listOperationName,
      query => {
        const annotationBelongsToQuery = doesAnnotationBelongToQueryVariables(
          additionalProps,
          query.variables,
          annotationSubject
        )

        return (
          annotationBelongsToQuery &&
          !!query.variables &&
          getAnnotationStatus(query.variables) === 'RESOLVED_ONLY'
        )
      }
    )

    cachedListQueriesToAdd.forEach(({ variables }) => {
      if (!variables) {
        return
      }

      if (isPreview) {
        // For preview queries, manually update the cache
        const previewVariables =
          variables as GetPreviewAnnotationsQueryVariables
        const query = apollo.readQuery<
          GetPreviewAnnotationsQuery,
          GetPreviewAnnotationsQueryVariables
        >({
          query: GetPreviewAnnotationsDocument,
          variables: previewVariables,
        })

        if (query) {
          apollo.writeQuery<
            GetPreviewAnnotationsQuery,
            GetPreviewAnnotationsQueryVariables
          >({
            query: GetPreviewAnnotationsDocument,
            variables: previewVariables,
            data: produce(query, draft => {
              draft.annotations.entries.unshift(baseAnnotation)
              draft.annotations.meta.totalCount++
            }),
          })
        }
      } else {
        // Use existing helpers for regular queries
        updateAnnotationsQuery(
          apollo,
          variables as GetAnnotationsQueryVariables,
          ({ annotations }) => {
            annotations.entries.unshift(baseAnnotation)
            annotations.meta.totalCount++
          }
        )
      }
    })
  }

  /*
   * `getBaseAnnotation` will return null if the user is not viewing the
   * `Comments` tab in the sidebar.
   *
   * To ensure that the annotation dots are updated correctly, we read and
   * update them separately.
   */
  const annotationDot = getAnnotationDot(apollo, annotation.identifier)
  if (annotationDot) {
    const dotOperationName = isPreview
      ? 'getPreviewAnnotationDots'
      : 'getAnnotationDots'
    const cachedDotQueriesToAdd = getObservableQueriesByOperationName(
      apollo,
      dotOperationName,
      query => {
        const annotationBelongsToQuery = doesAnnotationBelongToQueryVariables(
          additionalProps,
          query.variables,
          annotationSubject
        )

        return (
          annotationBelongsToQuery &&
          !!query.variables &&
          getAnnotationStatus(query.variables) === 'RESOLVED_ONLY'
        )
      }
    )

    cachedDotQueriesToAdd.forEach(({ variables }) => {
      if (!variables) {
        return
      }

      if (isPreview) {
        // For preview queries, manually update the cache
        const previewVariables =
          variables as GetPreviewAnnotationDotsQueryVariables
        const query = apollo.readQuery<
          GetPreviewAnnotationDotsQuery,
          GetPreviewAnnotationDotsQueryVariables
        >({
          query: GetPreviewAnnotationDotsDocument,
          variables: previewVariables,
        })

        if (query) {
          apollo.writeQuery<
            GetPreviewAnnotationDotsQuery,
            GetPreviewAnnotationDotsQueryVariables
          >({
            query: GetPreviewAnnotationDotsDocument,
            variables: previewVariables,
            data: produce(query, draft => {
              draft.previewAnnotations.entries.push(annotationDot)
              draft.previewAnnotations.meta.totalCount++
            }),
          })
        }
      } else {
        // Use existing helpers for regular queries
        updateAnnotationsDotsQuery(
          apollo,
          variables as GetAnnotationDotsQueryVariables,
          ({ annotations }) => {
            annotations.entries.push(annotationDot)
            annotations.meta.totalCount++
          }
        )
      }
    })
  }
}

export const unResolveAnnotation = (
  apollo: ApolloClient<object>,
  additionalProps: AdditionalProps,
  annotation: AnnotationSubjectFragment,
  isPreview = false
) => {
  /**
   * In order to successfully un-resolve an annotation
   * we need to:
   * [programmatically]
   * - remove the annotation from all the resolved queries [1]
   * - add the annotation to the active queries [2]
   *
   * [fragment BaseAnnotation refetch]
   * - Update the subject annotation counters
   * - Update the resolution status
   */
  const { identifier } = annotation

  // [1] Remove the annotation from all the resolved queries
  removeFromPaginated(apollo, { __typename: 'Annotation', identifier }, key =>
    key.includes('RESOLVED_ONLY')
  )

  // [2] Add the annotation to the active queries
  const annotationSubject = getCacheInputFromAnnotationSubject(annotation)

  if (annotationSubject === null) {
    /**
     * If the subject is "UnprocessedSubject" which should never be the case (for FE)
     * "getCacheInputFromAnnotationSubject" will return null.
     * Since FE can't handle this state, there is no need to proceed with the
     * cache operations
     */
    return
  }

  /**
   * To prevent including the "BaseAnnotation" fragment on the
   * "unresolveAnnotation" mutation, it would force a huge optimistic response,
   * it was opted to get the base annotation fragment isolated.
   *
   * Since this fragment will only be relevant when the active sidebar is visible,
   * this doesn't occur when you are un-resolving the annotation on the resolved sidebar (filter)
   *
   * If the user is un-resolving a annotation (in the popover) with the active sidebar visible on the side
   * the "annotationComments" query already includes the "BaseAnnotation" fragment
   * to make sure the sidebar is updated has it included
   */
  const baseAnnotation = getBaseAnnotation(apollo, annotation.identifier)
  if (baseAnnotation) {
    const listOperationName = isPreview
      ? 'getPreviewAnnotations'
      : 'getAnnotations'
    const cachedListsQueriesToAdd = getObservableQueriesByOperationName(
      apollo,
      listOperationName,
      ({ variables }) =>
        doesAnnotationBelongToQueryVariables(
          additionalProps,
          variables,
          annotationSubject
        ) &&
        !!variables &&
        getAnnotationStatus(variables) !== 'RESOLVED_ONLY'
    )

    cachedListsQueriesToAdd.forEach(({ variables }) => {
      if (!variables) {
        return
      }

      if (isPreview) {
        // For preview queries, manually update the cache
        const previewVariables =
          variables as GetPreviewAnnotationsQueryVariables
        const query = apollo.readQuery<
          GetPreviewAnnotationsQuery,
          GetPreviewAnnotationsQueryVariables
        >({
          query: GetPreviewAnnotationsDocument,
          variables: previewVariables,
        })

        if (query) {
          apollo.writeQuery<
            GetPreviewAnnotationsQuery,
            GetPreviewAnnotationsQueryVariables
          >({
            query: GetPreviewAnnotationsDocument,
            variables: previewVariables,
            data: produce(query, draft => {
              const doesAnnotationAlreadyExist = draft.annotations.entries.find(
                ({ identifier }) => baseAnnotation.identifier === identifier
              )
              if (!doesAnnotationAlreadyExist) {
                draft.annotations.entries.unshift(baseAnnotation)
                draft.annotations.meta.totalCount++
              }
            }),
          })
        }
      } else {
        // Use existing helpers for regular queries
        if (
          getSortOrder(variables) === 'NEW_FIRST' &&
          getAnnotationStatus(variables) === 'ACTIVE_ONLY'
        ) {
          // Add to the Active dot list
          updateAnnotationsInboxOrder(
            apollo,
            variables as GetAnnotationsQueryVariables,
            baseAnnotation
          )
        } else {
          updateAnnotationsAllOrder(
            apollo,
            variables as GetAnnotationsQueryVariables,
            baseAnnotation
          )
        }
      }
    })
  }

  /*
   * `getBaseAnnotation` will return null if the user is not viewing the
   * `Comments` tab in the sidebar.
   *
   * To ensure that the annotation dots are updated correctly, we read and
   * update them separately.
   */
  const annotationDot = getAnnotationDot(apollo, annotation.identifier)
  if (annotationDot) {
    const dotOperationName = isPreview
      ? 'getPreviewAnnotationDots'
      : 'getAnnotationDots'
    const cachedDotQueriesToAdd = getObservableQueriesByOperationName(
      apollo,
      dotOperationName,
      ({ variables }) =>
        doesAnnotationBelongToQueryVariables(
          additionalProps,
          variables,
          annotationSubject
        ) &&
        !!variables &&
        getAnnotationStatus(variables) !== 'RESOLVED_ONLY'
    )

    const addAnnotation = ({
      annotations,
    }: WritableDraft<GetAnnotationDotsQuery>) => {
      /**
       * Double check if the annotation already exists
       * because if it does we shouldn't re-add it
       */
      const doesAnnotationAlreadyExist = annotations.entries.find(
        ({ identifier }) => annotation.identifier === identifier
      )

      if (!doesAnnotationAlreadyExist) {
        annotations.entries.unshift(annotationDot)
        annotations.meta.totalCount++
      }
    }

    cachedDotQueriesToAdd.forEach(({ variables }) => {
      if (!variables) {
        return
      }

      if (isPreview) {
        // For preview queries, manually update the cache
        const previewVariables =
          variables as GetPreviewAnnotationDotsQueryVariables
        const query = apollo.readQuery<
          GetPreviewAnnotationDotsQuery,
          GetPreviewAnnotationDotsQueryVariables
        >({
          query: GetPreviewAnnotationDotsDocument,
          variables: previewVariables,
        })

        if (query) {
          apollo.writeQuery<
            GetPreviewAnnotationDotsQuery,
            GetPreviewAnnotationDotsQueryVariables
          >({
            query: GetPreviewAnnotationDotsDocument,
            variables: previewVariables,
            data: produce(query, draft => {
              addAnnotation(draft as any)
            }),
          })
        }
      } else {
        // Use existing helpers for regular queries
        updateAnnotationsDotsQuery(
          apollo,
          variables as GetAnnotationDotsQueryVariables,
          addAnnotation
        )
      }
    })
  }
}
